/**
 * Assisted painting (14.4, the reference's paint tool).
 *
 * Five actions, matching it: paint the part, paint an edge, paint a face, pick
 * a material up, and clear one. What makes it worth having is not the painting
 * — the properties form can already set a material — but the *reach*: putting
 * banding on forty parts by clicking forty edges in the model, instead of
 * finding forty rows in a table.
 *
 * The edge and face assignments live on the part's own properties rather than
 * on the element's material, because that is where the cut list reads them
 * from: an edge band is not the element's material, it is a strip glued to one
 * of its sides.
 */

import type { SceneElement } from "@calcplan/domain";
import type { StructureResult } from "./operations";
import type { EdgeSide, FaceSide } from "../model/part";

export type PaintTarget =
  | { kind: "part" }
  | { kind: "edge"; side: EdgeSide }
  | { kind: "face"; side: FaceSide };

export interface PaintOptions {
  /**
   * `instance` paints only what was clicked; `definition` paints every element
   * that shares the same definition. The reference offers the same choice, and
   * the second is the one that makes the tool worth using.
   */
  reach: "instance" | "definition";
}

/** Where a part's edge and face materials are kept on the element. */
const EDGE_KEY: Record<EdgeSide, string> = {
  ymin: "cutlistEdgeYmin",
  ymax: "cutlistEdgeYmax",
  xmin: "cutlistEdgeXmin",
  xmax: "cutlistEdgeXmax",
};

const FACE_KEY: Record<FaceSide, string> = {
  zmin: "cutlistFaceZmin",
  zmax: "cutlistFaceZmax",
};

/**
 * Which elements an action reaches.
 *
 * With `definition` reach, everything built from the same catalogue item — or,
 * failing that, sharing the same name — is included. A library part placed
 * twelve times is one part with twelve instances, and painting one of them and
 * not the others is almost never what was meant.
 */
export function paintReach(
  elements: readonly SceneElement[],
  seedIds: readonly string[],
  reach: PaintOptions["reach"],
): string[] {
  if (reach === "instance") return [...new Set(seedIds)];
  const seeds = elements.filter((element) => seedIds.includes(element.id));
  const itemIds = new Set(seeds.map((element) => element.catalogRef?.itemId).filter(Boolean) as string[]);
  const names = new Set(seeds.filter((element) => !element.catalogRef).map((element) => element.name));
  const reached = elements.filter((element) =>
    seedIds.includes(element.id)
    || (element.catalogRef?.itemId !== undefined && itemIds.has(element.catalogRef.itemId))
    || (!element.catalogRef && names.has(element.name)));
  return [...new Set(reached.map((element) => element.id))];
}

/**
 * Puts a material on the part, on one of its edges, or on one of its faces.
 *
 * `materialId` empty clears it, which is the reference's fifth action rather
 * than a separate operation — clearing is painting with nothing.
 */
export function paint(
  elements: readonly SceneElement[],
  ids: readonly string[],
  target: PaintTarget,
  materialId: string,
): StructureResult {
  const targets = new Set(ids);
  const changed: string[] = [];
  let lockedCount = 0;

  const next = elements.map((element) => {
    if (!targets.has(element.id)) return element;
    if (element.locked) {
      lockedCount += 1;
      return element;
    }

    if (target.kind === "part") {
      if (element.materialId === materialId) return element;
      changed.push(element.id);
      return {
        ...element,
        materialId,
        // The PBR assignment belongs to the renderer and is set separately; a
        // cleared material must not leave a stale one drawn.
        materialAssignments: materialId ? element.materialAssignments : {},
        revision: element.revision + 1,
      };
    }

    const key = target.kind === "edge" ? EDGE_KEY[target.side] : FACE_KEY[target.side];
    const current = element.extensions[key];
    if ((current ?? "") === materialId) return element;
    const extensions = { ...element.extensions };
    if (materialId) extensions[key] = materialId;
    else delete extensions[key];
    changed.push(element.id);
    return { ...element, extensions, revision: element.revision + 1 };
  });

  return {
    elements: next,
    changed,
    error: changed.length === 0 && lockedCount
      ? "Os elementos alcançados estão travados."
      : undefined,
  };
}

/**
 * Reads back what is on a part, an edge or a face — the eyedropper.
 *
 * Answering `null` when nothing is there is deliberate: an empty string would
 * be indistinguishable from "cleared", and the tool has to be able to say
 * "there is nothing here to pick up".
 */
export function pickMaterial(
  elements: readonly SceneElement[],
  elementId: string,
  target: PaintTarget,
): string | null {
  const element = elements.find((candidate) => candidate.id === elementId);
  if (!element) return null;
  if (target.kind === "part") return element.materialId || null;
  const key = target.kind === "edge" ? EDGE_KEY[target.side] : FACE_KEY[target.side];
  const value = element.extensions[key];
  return typeof value === "string" && value ? value : null;
}

/**
 * What a paint action would touch, so the count can be shown before the click
 * lands. Painting forty parts by accident is the failure mode this prevents.
 */
export interface PaintPreview {
  reached: number;
  wouldChange: number;
  locked: number;
}

export function previewPaint(
  elements: readonly SceneElement[],
  seedIds: readonly string[],
  target: PaintTarget,
  materialId: string,
  reach: PaintOptions["reach"],
): PaintPreview {
  const ids = paintReach(elements, seedIds, reach);
  const result = paint(elements, ids, target, materialId);
  const locked = elements.filter((element) => ids.includes(element.id) && element.locked).length;
  return { reached: ids.length, wouldChange: result.changed.length, locked };
}
